import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 })}`;
};

export default function PricingEditor() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/pricing`);
      const data = await res.json();
      // features -> newline string for editing
      data.plans = (data.plans || []).map((p) => ({ ...p, _featuresText: (p.features || []).join('\n') }));
      data._referralText = (data.referral_sources || []).join('\n');
      setConfig(data);
    } catch (err) {
      setError('Failed to load pricing config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTop = (field, value) => setConfig((c) => ({ ...c, [field]: value }));

  const updatePlan = (idx, field, value) =>
    setConfig((c) => {
      const plans = [...c.plans];
      plans[idx] = { ...plans[idx], [field]: value };
      return { ...c, plans };
    });

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        headline: config.headline || '',
        subheadline: config.subheadline || '',
        footer_note: config.footer_note || '',
        footer_contact: config.footer_contact || '',
        referral_sources: (config._referralText || '').split('\n').map((s) => s.trim()).filter(Boolean),
        plans: config.plans.map((p) => ({
          key: p.key,
          name: p.name,
          description: p.description || '',
          monthly_amount: parseFloat(p.monthly_amount) || 0,
          yearly_amount: parseFloat(p.yearly_amount) || 0,
          daily_amount: parseFloat(p.daily_amount) || 0,
          savings_label: p.savings_label || '',
          features: (p._featuresText || '').split('\n').map((f) => f.trim()).filter(Boolean),
          coming_soon: !!p.coming_soon,
          monthly_plan_id: p.monthly_plan_id,
          yearly_plan_id: p.yearly_plan_id || '',
          daily_plan_id: p.daily_plan_id || '',
          trial_days: p.trial_days || 7,
          feature_flags: p.feature_flags || [],
        })),
      };
      const res = await fetch(`${API_URL}/api/subscriptions/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      setSuccess('Pricing updated. Changes are live on the pricing page and Stripe checkout.');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-400 py-8">Loading pricing…</div>;
  if (!config) return <div className="text-red-400 py-8">Could not load pricing config.</div>;

  const inputCls =
    'w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500';

  return (
    <div data-testid="pricing-editor">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Pricing</h1>
          <p className="text-slate-400 text-sm">
            Edit the pricing page. The price shown here is the exact amount charged at Stripe checkout.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 text-white font-medium rounded-lg transition"
          data-testid="pricing-save-btn"
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {error && <div className="mb-4 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg" data-testid="pricing-editor-error">{error}</div>}
      {success && <div className="mb-4 bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 px-4 py-3 rounded-lg" data-testid="pricing-editor-success">{success}</div>}

      {/* Page text */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Page Text</h2>
        <div className="grid gap-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Headline</label>
            <input className={inputCls} value={config.headline || ''} onChange={(e) => updateTop('headline', e.target.value)} data-testid="pricing-headline-input" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Subheadline</label>
            <input className={inputCls} value={config.subheadline || ''} onChange={(e) => updateTop('subheadline', e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Footer note</label>
              <input className={inputCls} value={config.footer_note || ''} onChange={(e) => updateTop('footer_note', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Footer contact</label>
              <input className={inputCls} value={config.footer_contact || ''} onChange={(e) => updateTop('footer_contact', e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-6">
        {config.plans.map((plan, idx) => (
          <div key={plan.key} className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid={`pricing-plan-editor-${plan.key}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{plan.name || plan.key}</h3>
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!plan.coming_soon}
                  onChange={(e) => updatePlan(idx, 'coming_soon', e.target.checked)}
                  className="w-4 h-4 accent-amber-500"
                  data-testid={`pricing-coming-soon-${plan.key}`}
                />
                Coming Soon
              </label>
            </div>

            <div className="grid gap-3">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Plan name</label>
                <input className={inputCls} value={plan.name || ''} onChange={(e) => updatePlan(idx, 'name', e.target.value)} data-testid={`pricing-name-${plan.key}`} />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Description</label>
                <input className={inputCls} value={plan.description || ''} onChange={(e) => updatePlan(idx, 'description', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Monthly amount ($)</label>
                  <input type="number" min="0" step="0.01" className={inputCls} value={plan.monthly_amount} onChange={(e) => updatePlan(idx, 'monthly_amount', e.target.value)} data-testid={`pricing-monthly-${plan.key}`} />
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Day pass ($, one-time)</label>
                  <input type="number" min="0" step="0.01" className={inputCls} value={plan.daily_amount ?? ''} onChange={(e) => updatePlan(idx, 'daily_amount', e.target.value)} data-testid={`pricing-daily-${plan.key}`} />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Savings label (under price)</label>
                <input className={inputCls} value={plan.savings_label || ''} onChange={(e) => updatePlan(idx, 'savings_label', e.target.value)} placeholder="e.g. or $1,290/year (save $258)" />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Feature bullets (one per line)</label>
                <textarea
                  rows={7}
                  className={inputCls}
                  value={plan._featuresText || ''}
                  onChange={(e) => updatePlan(idx, '_featuresText', e.target.value)}
                  data-testid={`pricing-features-${plan.key}`}
                />
              </div>
              <p className="text-xs text-slate-500">
                Charge preview: monthly {money(plan.monthly_amount)} · day pass {money(plan.daily_amount)}
                {plan.coming_soon && ' · hidden behind "Coming Soon" (not purchasable)'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Signup: "How did you hear about us" options */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mt-6" data-testid="referral-sources-editor">
        <h2 className="text-lg font-semibold text-white mb-1">Signup: "How did you hear about IntelliTix?"</h2>
        <p className="text-slate-400 text-sm mb-4">
          These options appear in the signup dropdown. One option per line.
          <span className="text-slate-500"> "Please Select" and "Other" (with a free-text box) are added automatically.</span>
        </p>
        <textarea
          rows={7}
          className={inputCls}
          value={config._referralText || ''}
          onChange={(e) => updateTop('_referralText', e.target.value)}
          placeholder={'Google / Search Engine\nSocial Media\nFriend or Colleague\nYouTube'}
          data-testid="referral-sources-input"
        />
        <p className="text-xs text-slate-500 mt-2">Saved with the Save Changes button above.</p>
      </div>
    </div>
  );
}
